import React, { useEffect, useState, useRef, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { PlaneIcon, ClockIcon, ShieldCheckIcon, WalletIcon, ArrowRightIcon, CheckCircleIcon, SparklesIcon, ZapIcon, TrendingUpIcon, UsersIcon, AwardIcon, LockIcon, PhoneIcon, MailIcon } from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
// Animated Counter Component
function AnimatedCounter({
  end,
  duration = 2,
  suffix = ''
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number;
    let animationFrame: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isInView]);
  return <span ref={ref} data-id="element-1036">
      {count.toLocaleString()}
      {suffix}
    </span>;
}
// Enhanced 3D Plane Animation with Trail
function AnimatedPlane({
  delay = 0
}: {
  delay?: number;
}) {
  return <motion.div className="absolute pointer-events-none" initial={{
    x: '-15%',
    y: '20%',
    opacity: 0
  }} animate={{
    x: ['15%', '110%'],
    y: ['20%', '10%'],
    opacity: [0, 1, 1, 0]
  }} transition={{
    duration: 12,
    repeat: Infinity,
    ease: 'easeInOut',
    delay,
    repeatDelay: 8
  }} data-id="element-1037">
      {/* Vapor Trail */}
      <motion.div className="absolute top-1/2 right-full w-32 h-1" animate={{
      opacity: [0.3, 0.6, 0.3],
      scaleX: [0.8, 1, 0.8]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1038">
        <div className="w-full h-full bg-gradient-to-l from-primary-400/40 via-primary-300/30 to-transparent rounded-full blur-sm" data-id="element-1039" />
      </motion.div>

      {/* Plane Body */}
      <motion.div animate={{
      rotate: [0, -2, 0],
      y: [0, -3, 0]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1040">
        <svg width="100" height="100" viewBox="0 0 100 100" fill="none" className="drop-shadow-2xl" data-id="element-1041">
          {/* Main fuselage */}
          <ellipse cx="50" cy="50" rx="35" ry="8" fill="url(#planeGradient)" opacity="0.95" data-id="element-1042" />
          {/* Wings */}
          <path d="M20 50 L50 35 L80 50 L50 52 Z" fill="url(#wingGradient)" opacity="0.9" data-id="element-1043" />
          {/* Tail */}
          <path d="M50 52 L50 65 L45 70 L55 70 Z" fill="url(#tailGradient)" data-id="element-1044" />
          {/* Window highlights */}
          <circle cx="40" cy="48" r="2" fill="white" opacity="0.6" data-id="element-1045" />
          <circle cx="50" cy="48" r="2" fill="white" opacity="0.6" data-id="element-1046" />
          <circle cx="60" cy="48" r="2" fill="white" opacity="0.6" data-id="element-1047" />

          <defs data-id="element-1048">
            <linearGradient id="planeGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-1049">
              <stop offset="0%" stopColor="#0066FF" data-id="element-1050" />
              <stop offset="100%" stopColor="#0052CC" data-id="element-1051" />
            </linearGradient>
            <linearGradient id="wingGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-1052">
              <stop offset="0%" stopColor="#0066FF" stopOpacity="0.8" data-id="element-1053" />
              <stop offset="100%" stopColor="#004999" stopOpacity="0.9" data-id="element-1054" />
            </linearGradient>
            <linearGradient id="tailGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-1055">
              <stop offset="0%" stopColor="#0066FF" data-id="element-1056" />
              <stop offset="100%" stopColor="#003D7A" data-id="element-1057" />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>

      {/* Sparkle effect */}
      <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" animate={{
      scale: [0, 1.5, 0],
      opacity: [0, 0.6, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeOut'
    }} data-id="element-1058">
        <div className="w-20 h-20 bg-primary-400/30 rounded-full blur-xl" data-id="element-1059" />
      </motion.div>
    </motion.div>;
}
// Airport Runway Lights Animation
function RunwayLights() {
  return <div className="absolute bottom-0 left-0 right-0 h-2 flex items-center justify-center gap-8 opacity-20" data-id="element-1060">
      {[...Array(12)].map((_, i) => <motion.div key={i} className="w-2 h-2 rounded-full bg-primary-500" animate={{
      opacity: [0.3, 1, 0.3],
      scale: [0.8, 1.2, 0.8]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      delay: i * 0.15,
      ease: 'easeInOut'
    }} data-id="element-1061" />)}
    </div>;
}
// Enhanced Cloud with depth
function Cloud({
  delay = 0,
  duration = 30,
  top = '20%',
  size = 1
}: {
  delay?: number;
  duration?: number;
  top?: string;
  size?: number;
}) {
  return <motion.div className="absolute" style={{
    top
  }} initial={{
    x: '-15%'
  }} animate={{
    x: '115%'
  }} transition={{
    duration,
    repeat: Infinity,
    ease: 'linear',
    delay
  }} data-id="element-1062">
      <motion.svg width={200 * size} height={100 * size} viewBox="0 0 200 100" fill="none" animate={{
      y: [0, -8, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1063">
        <ellipse cx="50" cy="60" rx="35" ry="25" fill="#0066FF" opacity="0.08" data-id="element-1064" />
        <ellipse cx="80" cy="50" rx="45" ry="30" fill="#0066FF" opacity="0.12" data-id="element-1065" />
        <ellipse cx="120" cy="55" rx="40" ry="28" fill="#0066FF" opacity="0.10" data-id="element-1066" />
        <ellipse cx="150" cy="60" rx="35" ry="25" fill="#0066FF" opacity="0.08" data-id="element-1067" />
      </motion.svg>
    </motion.div>;
}
// Control Tower Animation
function ControlTower() {
  return <motion.div className="absolute bottom-20 right-10 opacity-15" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 0.15,
    y: 0
  }} transition={{
    duration: 1
  }} data-id="element-1068">
      <svg width="80" height="120" viewBox="0 0 80 120" fill="none" data-id="element-1069">
        {/* Tower base */}
        <rect x="25" y="80" width="30" height="40" fill="#0066FF" opacity="0.6" data-id="element-1070" />
        {/* Tower top */}
        <path d="M20 80 L60 80 L50 40 L30 40 Z" fill="#0066FF" opacity="0.8" data-id="element-1071" />
        {/* Windows */}
        <rect x="32" y="50" width="6" height="8" fill="white" opacity="0.4" data-id="element-1072" />
        <rect x="42" y="50" width="6" height="8" fill="white" opacity="0.4" data-id="element-1073" />
        {/* Antenna */}
        <line x1="40" y1="40" x2="40" y2="20" stroke="#0066FF" strokeWidth="2" opacity="0.6" data-id="element-1074" />
        <circle cx="40" cy="20" r="3" fill="#FFB300" opacity="0.8" data-id="element-1075">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" data-id="element-1076" />
        </circle>
      </svg>
    </motion.div>;
}
// Floating Airport Elements
function AirportScene() {
  return <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1077">
      {/* Multiple planes at different depths */}
      <AnimatedPlane delay={0} data-id="element-1078" />
      <AnimatedPlane delay={6} data-id="element-1079" />
      <AnimatedPlane delay={12} data-id="element-1080" />

      {/* Enhanced clouds at different layers */}
      <Cloud delay={0} duration={35} top="15%" size={1.2} data-id="element-1081" />
      <Cloud delay={8} duration={40} top="25%" size={0.9} data-id="element-1082" />
      <Cloud delay={16} duration={45} top="35%" size={1.1} data-id="element-1083" />
      <Cloud delay={24} duration={38} top="45%" size={0.8} data-id="element-1084" />

      {/* Control Tower */}
      <ControlTower data-id="element-1085" />

      {/* Runway Lights */}
      <RunwayLights data-id="element-1086" />

      {/* Floating particles */}
      {[...Array(20)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-primary-400 rounded-full" style={{
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    }} animate={{
      y: [0, -30, 0],
      opacity: [0, 0.6, 0]
    }} transition={{
      duration: 3 + Math.random() * 2,
      repeat: Infinity,
      delay: Math.random() * 5,
      ease: 'easeInOut'
    }} data-id="element-1087" />)}
    </div>;
}
const stats = [{
  value: 15000,
  label: 'Refunds Processed',
  suffix: '+'
}, {
  value: 3,
  label: 'Billion Naira Refunded',
  suffix: 'B+',
  prefix: '₦'
}, {
  value: 25,
  label: 'Average Processing Time',
  suffix: ' min'
}, {
  value: 99.9,
  label: 'Success Rate',
  suffix: '%'
}];
const features = [{
  icon: ZapIcon,
  title: 'Lightning Fast',
  description: 'Get your refund in under 30 minutes. Our automated system works 24/7 to process your request instantly.',
  color: 'from-primary-500 to-primary-600'
}, {
  icon: SparklesIcon,
  title: '10% Bonus Credit',
  description: 'Choose store credit and get 10% extra. Use it to book any flight instantly with zero waiting time.',
  color: 'from-accent-500 to-accent-600'
}, {
  icon: ShieldCheckIcon,
  title: 'NCAA Compliant',
  description: 'Fully compliant with Nigerian Civil Aviation Authority regulations. Your rights are protected.',
  color: 'from-success-500 to-success-600'
}, {
  icon: LockIcon,
  title: 'Bank-Level Security',
  description: 'Your data is encrypted with AES-256. NDPR compliant and PCI-DSS certified for your peace of mind.',
  color: 'from-surface-700 to-surface-800'
}];
const steps = [{
  number: '01',
  icon: PlaneIcon,
  title: 'Enter Ticket Details',
  description: 'Simply enter your PNR or ticket number. We validate it instantly with the airline.'
}, {
  number: '02',
  icon: WalletIcon,
  title: 'Choose Your Refund',
  description: 'Get instant bank payout or store credit with a 10% bonus for future bookings.'
}, {
  number: '03',
  icon: ZapIcon,
  title: 'Get Money in Minutes',
  description: 'Your refund is processed in under 30 minutes. No waiting, no hassle.'
}];
const testimonials = [{
  name: 'Chioma Okonkwo',
  role: 'Business Traveler',
  content: 'Got my ₦45,000 refund in 20 minutes! AeroZeep made what used to take weeks happen in minutes.',
  rating: 5
}, {
  name: 'Emeka Eze',
  role: 'Frequent Flyer',
  content: 'The store credit bonus is brilliant. I saved 10% and booked my next flight immediately.',
  rating: 5
}, {
  name: 'Ngozi Nwosu',
  role: 'Travel Agent',
  content: 'Finally, a refund service that actually works. My clients love how fast and transparent it is.',
  rating: 5
}];
const trustBadges = [{
  icon: ShieldCheckIcon,
  label: 'NCAA Compliant'
}, {
  icon: LockIcon,
  label: 'NDPR Secure'
}, {
  icon: CheckCircleIcon,
  label: 'PCI-DSS Certified'
}, {
  icon: AwardIcon,
  label: '99.9% Uptime'
}];
export function LandingPage() {
  const {
    scrollY
  } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 300], [1, 0.95]);
  return <div className="min-h-screen bg-gradient-to-b from-white via-primary-50/30 to-white overflow-hidden relative" data-id="element-1088">
      <Header data-id="element-1089" />

      {/* Enhanced Airport Scene Background */}
      <AirportScene data-id="element-1090" />

      {/* Hero Section */}
      <motion.section style={{
      opacity: heroOpacity,
      scale: heroScale
    }} className="relative pt-12 sm:pt-16 lg:pt-20 pb-20 sm:pb-28 lg:pb-32 px-4 sm:px-6 lg:px-8" data-id="element-1091">
        <div className="max-w-7xl mx-auto" data-id="element-1092">
          <div className="text-center max-w-4xl mx-auto" data-id="element-1093">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-primary-100 to-accent-100 rounded-full mb-6 sm:mb-8" data-id="element-1094">
              <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-1095">
                <SparklesIcon className="w-4 h-4 text-primary-600" data-id="element-1096" />
              </motion.div>
              <span className="text-xs sm:text-sm font-semibold text-primary-700" data-id="element-1097">
                Nigeria's #1 Airline Refund Platform
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] xl:text-[6rem] font-extrabold mb-4 sm:mb-6 leading-tight" data-id="element-1098">
              Get Your Airline
              <br data-id="element-1099" />
              <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent" data-id="element-1100">
                Refund in Minutes
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="text-base sm:text-lg md:text-xl lg:text-2xl text-surface-600 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4" data-id="element-1101">
              Fast, transparent, and fully compliant with NCAA regulations.
              <span className="font-semibold text-surface-900" data-id="element-1102">
                {' '}
                Get your money back
              </span>{' '}
              or choose
              <span className="font-semibold text-accent-600" data-id="element-1103">
                {' '}
                store credit with 10% bonus
              </span>
              .
            </motion.p>

            {/* CTA Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }} className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4" data-id="element-1104">
              <Link to={ROUTES.REFUND} className="w-full sm:w-auto" data-id="element-1105">
                <Button size="lg" className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 shadow-glow hover:shadow-xl transition-all duration-300" icon={<ArrowRightIcon className="w-4 sm:w-5 h-4 sm:h-5" data-id="element-1107" />} iconPosition="right" data-id="element-1106">
                  Start Refund Process
                </Button>
              </Link>
              <Link to={ROUTES.SEARCH} className="w-full sm:w-auto" data-id="element-1108">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4" data-id="element-1109">
                  Book a Flight
                </Button>
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.8
          }} className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 px-4" data-id="element-1110">
              {trustBadges.map((badge, index) => {
              const Icon = badge.icon;
              return <motion.div key={badge.label} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.8 + index * 0.1
              }} className="flex items-center gap-2 text-surface-600" data-id="element-1111">
                    <Icon className="w-4 sm:w-5 h-4 sm:h-5 text-primary-500 flex-shrink-0" data-id="element-1112" />
                    <span className="text-xs sm:text-sm font-medium whitespace-nowrap" data-id="element-1113">
                      {badge.label}
                    </span>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>

        {/* Enhanced Decorative Elements */}
        <motion.div className="absolute top-20 left-4 sm:left-10 w-24 sm:w-32 h-24 sm:h-32 bg-primary-300 rounded-full blur-3xl" animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-1114" />
        <motion.div className="absolute bottom-20 right-4 sm:right-10 w-32 sm:w-40 h-32 sm:h-40 bg-accent-300 rounded-full blur-3xl" animate={{
        scale: [1, 1.3, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} data-id="element-1115" />
      </motion.section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden" data-id="element-1116">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-1117">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} data-id="element-1118" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-1119">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1120">
            {stats.map((stat, index) => <motion.div key={stat.label} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.6
          }} className="text-center" data-id="element-1121">
                <div className="text-4xl md:text-5xl font-extrabold text-white mb-2 tabular-nums" data-id="element-1122">
                  {stat.prefix}
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} data-id="element-1123" />
                </div>
                <p className="text-primary-100 font-medium" data-id="element-1124">{stat.label}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-white relative" data-id="element-1125">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1126">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-20" data-id="element-1127">
            <h2 className="text-display-md font-bold text-surface-900 mb-4" data-id="element-1128">
              Why Choose AeroZeep
            </h2>
            <p className="text-xl text-surface-600 max-w-2xl mx-auto" data-id="element-1129">
              We've built the most trusted and fastest airline refund platform
              in Nigeria
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1130">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15,
              duration: 0.6
            }} whileHover={{
              y: -8,
              transition: {
                duration: 0.2
              }
            }} data-id="element-1131">
                  <Card variant="elevated" padding="lg" className="h-full hover:shadow-elevated transition-shadow duration-300" data-id="element-1132">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-lg`} data-id="element-1133">
                      <Icon className="w-7 h-7 text-white" data-id="element-1134" />
                    </div>
                    <h3 className="text-heading-lg font-bold text-surface-900 mb-3" data-id="element-1135">
                      {feature.title}
                    </h3>
                    <p className="text-body-md text-surface-600 leading-relaxed" data-id="element-1136">
                      {feature.description}
                    </p>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-32 bg-gradient-to-b from-surface-50 to-white relative overflow-hidden" data-id="element-1137">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1138">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-20" data-id="element-1139">
            <h2 className="text-display-md font-bold text-surface-900 mb-4" data-id="element-1140">
              How It Works
            </h2>
            <p className="text-xl text-surface-600 max-w-2xl mx-auto" data-id="element-1141">
              Three simple steps to get your refund. No complicated forms, no
              waiting on hold.
            </p>
          </motion.div>

          <div className="relative" data-id="element-1142">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary-200 via-primary-300 to-primary-200 -translate-y-1/2" data-id="element-1143" />

            <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-1144">
              {steps.map((step, index) => {
              const Icon = step.icon;
              return <motion.div key={step.number} initial={{
                opacity: 0,
                scale: 0.8
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.2,
                duration: 0.6
              }} className="relative" data-id="element-1145">
                    <div className="text-center" data-id="element-1146">
                      {/* Step Number */}
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white text-2xl font-bold mb-6 shadow-glow relative z-10" data-id="element-1147">
                        {step.number}
                      </div>

                      {/* Icon */}
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-100 mb-6" data-id="element-1148">
                        <Icon className="w-8 h-8 text-primary-600" data-id="element-1149" />
                      </div>

                      <h3 className="text-heading-lg font-bold text-surface-900 mb-3" data-id="element-1150">
                        {step.title}
                      </h3>
                      <p className="text-body-md text-surface-600 leading-relaxed" data-id="element-1151">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-32 bg-white" data-id="element-1152">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1153">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-20" data-id="element-1154">
            <h2 className="text-display-md font-bold text-surface-900 mb-4" data-id="element-1155">
              Loved by Thousands
            </h2>
            <p className="text-xl text-surface-600 max-w-2xl mx-auto" data-id="element-1156">
              See what our customers say about their experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-1157">
            {testimonials.map((testimonial, index) => <motion.div key={testimonial.name} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15,
            duration: 0.6
          }} data-id="element-1158">
                <Card variant="elevated" padding="lg" className="h-full" data-id="element-1159">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4" data-id="element-1160">
                    {[...Array(testimonial.rating)].map((_, i) => <svg key={i} className="w-5 h-5 text-accent-500" fill="currentColor" viewBox="0 0 20 20" data-id="element-1161">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1162" />
                      </svg>)}
                  </div>

                  <p className="text-body-md text-surface-700 mb-6 leading-relaxed" data-id="element-1163">
                    "{testimonial.content}"
                  </p>

                  <div data-id="element-1164">
                    <p className="font-semibold text-surface-900" data-id="element-1165">
                      {testimonial.name}
                    </p>
                    <p className="text-body-sm text-surface-500" data-id="element-1166">
                      {testimonial.role}
                    </p>
                  </div>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Store Credit CTA */}
      <section className="py-24 bg-gradient-to-r from-accent-500 via-accent-600 to-accent-700 relative overflow-hidden" data-id="element-1167">
        <div className="absolute inset-0 opacity-10" data-id="element-1168">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '30px 30px'
        }} data-id="element-1169" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-1170">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12" data-id="element-1171">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="text-center lg:text-left lg:flex-1" data-id="element-1172">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6" data-id="element-1173">
                <SparklesIcon className="w-5 h-5 text-white" data-id="element-1174" />
                <span className="text-sm font-semibold text-white" data-id="element-1175">
                  Limited Time Offer
                </span>
              </div>
              <h2 className="text-display-md font-bold text-white mb-4" data-id="element-1176">
                Get 10% Extra with Store Credit
              </h2>
              <p className="text-xl text-white/90 max-w-xl" data-id="element-1177">
                Choose store credit instead of bank payout and get a 10% bonus.
                Use it to book any flight instantly — no waiting required.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-1178">
              <Link to={ROUTES.REFUND} data-id="element-1179">
                <Button size="lg" className="bg-white text-accent-600 hover:bg-white/90 text-lg px-8 py-4 shadow-xl" icon={<SparklesIcon className="w-5 h-5" data-id="element-1181" />} data-id="element-1180">
                  Claim Your Bonus
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-b from-white to-primary-50" data-id="element-1182">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1183">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1184">
            <h2 className="text-display-md font-bold text-surface-900 mb-6" data-id="element-1185">
              Ready to Get Your Refund?
            </h2>
            <p className="text-xl text-surface-600 mb-10 max-w-2xl mx-auto" data-id="element-1186">
              Join thousands of Nigerians who've already gotten their money
              back. Fast, transparent, and fully compliant.
            </p>
            <Link to={ROUTES.REFUND} data-id="element-1187">
              <Button size="lg" className="text-lg px-8 py-4 shadow-glow" icon={<ArrowRightIcon className="w-5 h-5" data-id="element-1189" />} iconPosition="right" data-id="element-1188">
                Start Refund Process
              </Button>
            </Link>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-12 text-surface-600" data-id="element-1190">
              <div className="flex items-center gap-2" data-id="element-1191">
                <PhoneIcon className="w-5 h-5" data-id="element-1192" />
                <span className="text-sm" data-id="element-1193">+234 800 000 0000</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1194">
                <MailIcon className="w-5 h-5" data-id="element-1195" />
                <span className="text-sm" data-id="element-1196">support@aerozeep.com</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1197" />
    </div>;
}